import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, CheckCircle, Cancel } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { surgeryTypeService } from '../../services/api';
import TopBar from '../../components/TopBar';

const emptyForm = { name: '', icd10Code: '', description: '', isActive: true };

const SurgeryTypesPage = () => {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await surgeryTypeService.getAll();
      setTypes(res.data);
    } catch {
      setError('Failed to load surgery types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setError('');
    setOpenDialog(true);
  };

  const openEdit = (row) => {
    setEditingItem(row);
    setForm({ name: row.name, icd10Code: row.icd10Code || '', description: row.description || '', isActive: row.isActive });
    setError('');
    setOpenDialog(true);
  };

  const handleClose = () => { setOpenDialog(false); setEditingItem(null); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Surgery type name is required.'); return; }
    if (!form.icd10Code.trim()) { setError('ICD-10 Code is required.'); return; }
    try {
      if (editingItem) {
        await surgeryTypeService.update(editingItem.id, form);
      } else {
        await surgeryTypeService.create(form);
      }
      handleClose();
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this surgery type?')) return;
    try {
      await surgeryTypeService.delete(id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete');
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'name', headerName: 'Surgery Type', flex: 1, minWidth: 180 },
    { field: 'icd10Code', headerName: 'ICD-10 Code', width: 130, valueFormatter: (p) => p.value || '—' },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200, valueFormatter: (p) => p.value || '—' },
    {
      field: 'hasMappings', headerName: 'Mapping', width: 110,
      renderCell: (p) => p.value
        ? <Chip size="small" label="Configured" color="success" variant="outlined" />
        : <Chip size="small" label="Not set" color="default" variant="outlined" />,
    },
    {
      field: 'isActive', headerName: 'Status', width: 100,
      renderCell: (p) => p.value
        ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}><CheckCircle fontSize="small" /><Typography variant="caption">Active</Typography></Box>
        : <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.disabled' }}><Cancel fontSize="small" /><Typography variant="caption">Inactive</Typography></Box>,
    },
    {
      field: 'actions', headerName: 'Actions', width: 120, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => openEdit(params.row)}><Edit fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><Delete fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Surgery Types" onBack={() => navigate('/dashboard')} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Define categories of surgeries. Use <strong>Surgery Type Mapping</strong> to configure required consent forms and pre-op checklist per type.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add Type</Button>
        </Paper>

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        <Paper elevation={3} sx={{ borderRadius: 2 }}>
          <DataGrid
            rows={types}
            columns={columns}
            loading={loading}
            autoHeight
            pageSize={20}
            rowsPerPageOptions={[20, 50]}
            disableSelectionOnClick
            rowHeight={52}
          />
        </Paper>
      </Container>

      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Surgery Type' : 'Add Surgery Type'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
            <TextField
              fullWidth required label="Surgery Type Name" sx={{ mb: 2 }}
              placeholder="e.g. LSCS, Appendectomy, Hernia Repair"
              value={form.name}
              onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
            />
            <TextField
              fullWidth required label="ICD-10 Code" sx={{ mb: 2 }}
              placeholder="e.g. K40.9, O82.0, K35.2"
              inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
              value={form.icd10Code}
              onChange={(e) => setForm(p => ({ ...p, icd10Code: e.target.value.toUpperCase() }))}
              helperText="International Classification of Diseases code for this surgery"
            />
            <TextField
              fullWidth label="Description" multiline rows={2} sx={{ mb: 2 }}
              placeholder="Brief description of this surgery type"
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
            />
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={(e) => setForm(p => ({ ...p, isActive: e.target.checked }))} color="success" />}
              label="Active"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">{editingItem ? 'Update' : 'Add'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default SurgeryTypesPage;
